import {
  DEFAULT_RENDER_DIAGNOSTICS,
  projectRenderState,
} from '../engine/rendering/RenderStateAdapter.js';
import { installRenderingTestApi } from '../devtools/RenderingTestApi.js';
import { CanvasWorldRenderer } from './CanvasWorldRenderer.js';

export const RENDERING_INTEGRATION_VERSION = 1;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  return value;
}

function normalizePosition(value, name = 'position') {
  assertPlainObject(value, name);
  const position = {};
  for (const axis of ['x', 'y', 'z']) {
    if (!Number.isFinite(value[axis])) throw new RangeError(`${name}.${axis} must be finite`);
    position[axis] = value[axis];
  }
  return position;
}

function freezeCamera(camera) {
  return Object.freeze({
    followSnake: camera.followSnake,
    followHeading: camera.followHeading,
    target: camera.target === null ? null : Object.freeze({ ...camera.target }),
    heading: camera.heading,
    zoom: camera.zoom,
    viewRadius: camera.viewRadius,
  });
}

export class RenderingIntegration {
  constructor({
    app,
    mount,
    windowRef = globalThis.window,
    documentRef = globalThis.document,
  }) {
    if (!app || typeof app !== 'object') throw new TypeError('app must be an object');
    if (!mount || typeof mount.querySelector !== 'function') {
      throw new TypeError('mount must be a DOM element');
    }
    assertMethod(app.snakeState, 'snapshot', 'app.snakeState');
    assertMethod(app.wildlifeSystem, 'snapshot', 'app.wildlifeSystem');
    assertMethod(app.traversalWorld, 'snapshot', 'app.traversalWorld');
    assertMethod(app.storySystem, 'snapshot', 'app.storySystem');
    assertMethod(app.simulationSnapshots, 'getStatus', 'app.simulationSnapshots');
    assertMethod(app.clock, 'snapshot', 'app.clock');
    this.app = app;
    this.mount = mount;
    this.windowRef = windowRef;
    this.documentRef = documentRef;
    this.renderer = null;
    this.engine2Host = null;
    this.legacyHost = null;
    this.shell = null;
    this.playControls = null;
    this.controlsMenu = null;
    this.controlsToggle = null;
    this.statusNode = null;
    this.mode = 'shell';
    this.installed = false;
    this.rafId = null;
    this.lastRenderState = null;
    this.lastIntegrationError = null;
    this.diagnostics = Object.freeze({ ...DEFAULT_RENDER_DIAGNOSTICS });
    this.camera = {
      followSnake: true,
      followHeading: true,
      target: null,
      heading: 0,
      zoom: 1,
      viewRadius: 8,
    };
    this.detachInputStep = null;
    this.onClick = (event) => this.#handleClick(event);
    this.onTestLabClick = (event) => this.#handleTestLabClick(event);
  }

  install() {
    if (this.installed) return this.getStatus();
    this.#installDom();
    this.renderer = new CanvasWorldRenderer({
      mount: this.engine2Host,
      windowRef: this.windowRef,
      documentRef: this.documentRef,
    });
    this.renderer.start({ visible: false });
    this.mount.addEventListener('click', this.onClick);
    this.#installTestLabControls();
    this.#installInputBridge();

    this.app.testApi = installRenderingTestApi({
      baseApi: this.app.testApi,
      integration: this,
      windowRef: this.windowRef,
    });
    if (this.app.testLab) this.app.testLab.testApi = this.app.testApi;
    this.app.rendering = this;
    this.installed = true;
    this.#syncStatus();
    this.#scheduleFrame();
    return this.getStatus();
  }

  destroy() {
    if (!this.installed) return this.getStatus();
    this.windowRef?.cancelAnimationFrame?.(this.rafId);
    this.rafId = null;
    this.detachInputStep?.();
    this.detachInputStep = null;
    this.mount.removeEventListener('click', this.onClick);
    this.app.testLab?.root?.removeEventListener('click', this.onTestLabClick);
    this.renderer?.destroy();
    this.renderer = null;
    this.engine2Host?.remove();
    this.statusNode?.closest?.('[data-rendering-status-row]')?.remove?.();
    this.engine2Host = null;
    this.installed = false;
    this.mode = 'shell';
    this.#syncStatus();
    return this.getStatus();
  }

  captureRenderState() {
    const latestStep = this.app.simulationSnapshots.getLatest?.() ?? null;
    const snapshotStatus = this.app.simulationSnapshots.getStatus();
    const snakeState = this.app.snakeState.snapshot();
    const camera = {
      target: this.camera.followSnake && snakeState
        ? snakeState.position
        : this.camera.target ?? snakeState?.position ?? { x: 0, y: 0, z: 0 },
      heading: this.camera.followHeading && snakeState
        ? snakeState.heading
        : this.camera.heading,
      zoom: this.camera.zoom,
      viewRadius: this.camera.viewRadius,
    };
    this.lastRenderState = projectRenderState({
      tick: latestStep?.tick ?? (snapshotStatus.nextTick > 0 ? snapshotStatus.nextTick - 1 : null),
      simulationTime: this.app.clock.snapshot().simulationTime,
      environment: {
        activeScenario: this.app.runtimeState.activeScenario,
        timeOfDay: this.app.runtimeState.timeOfDay,
        weather: this.app.runtimeState.weather,
      },
      snakeState,
      wildlifeState: this.app.wildlifeSystem.snapshot(),
      traversalState: this.app.traversalWorld.snapshot(),
      storyState: this.app.storySystem.snapshot(),
      camera,
      diagnostics: this.diagnostics,
    });
    return this.lastRenderState;
  }

  renderNow(timestamp = undefined) {
    if (!this.renderer) throw new Error('Rendering integration is not installed');
    const state = this.captureRenderState();
    const result = this.renderer.render(state, timestamp);
    this.lastIntegrationError = null;
    this.#syncStatus();
    return result;
  }

  showEngine2() {
    if (!this.renderer) throw new Error('Rendering integration is not installed');
    this.mode = 'engine2';
    this.shell.classList.add('is-playing', 'render-mode-engine2');
    this.shell.classList.remove('render-mode-legacy');
    this.documentRef.body?.classList?.add('game-active');
    this.engine2Host.hidden = false;
    this.legacyHost.hidden = true;
    this.playControls.hidden = false;
    this.controlsMenu.hidden = true;
    this.controlsToggle?.setAttribute('aria-expanded', 'false');
    this.renderer.setVisible(true);
    this.renderNow(this.windowRef?.performance?.now?.());
    this.renderer.focus();
    return this.getStatus();
  }

  showLegacy() {
    this.app.legacy?.launch?.();
    this.mode = 'legacy';
    this.shell.classList.add('is-playing', 'render-mode-legacy');
    this.shell.classList.remove('render-mode-engine2');
    this.documentRef.body?.classList?.add('game-active');
    this.engine2Host.hidden = true;
    this.legacyHost.hidden = false;
    this.playControls.hidden = false;
    this.controlsMenu.hidden = true;
    this.controlsToggle?.setAttribute('aria-expanded', 'false');
    this.renderer?.setVisible(false);
    this.app.legacy?.focus?.();
    this.#syncStatus();
    return this.getStatus();
  }

  exitPlayMode() {
    this.mode = 'shell';
    this.shell.classList.remove('is-playing', 'render-mode-engine2', 'render-mode-legacy');
    this.documentRef.body?.classList?.remove('game-active');
    this.engine2Host.hidden = true;
    this.legacyHost.hidden = false;
    this.playControls.hidden = true;
    this.controlsMenu.hidden = true;
    this.controlsToggle?.setAttribute('aria-expanded', 'false');
    this.renderer?.setVisible(false);
    this.windowRef?.scrollTo?.({ top: 0, behavior: 'instant' });
    this.#syncStatus();
    return this.getStatus();
  }

  stopRenderer() {
    if (this.mode === 'engine2') this.exitPlayMode();
    this.renderer?.stop();
    this.#syncStatus();
    return this.getStatus();
  }

  setDiagnostics(options = {}) {
    assertPlainObject(options, 'options');
    this.diagnostics = Object.freeze({
      showGrid: options.showGrid === undefined
        ? this.diagnostics.showGrid
        : Boolean(options.showGrid),
      showTraversal: options.showTraversal === undefined
        ? this.diagnostics.showTraversal
        : Boolean(options.showTraversal),
      showLabels: options.showLabels === undefined
        ? this.diagnostics.showLabels
        : Boolean(options.showLabels),
      showBounds: options.showBounds === undefined
        ? this.diagnostics.showBounds
        : Boolean(options.showBounds),
    });
    this.renderer?.setDiagnostics(this.diagnostics);
    if (this.mode === 'engine2') this.renderNow();
    this.#syncStatus();
    return this.getStatus();
  }

  setCamera(options = {}) {
    assertPlainObject(options, 'options');
    if (options.followSnake !== undefined) this.camera.followSnake = Boolean(options.followSnake);
    if (options.followHeading !== undefined) this.camera.followHeading = Boolean(options.followHeading);
    if (options.target !== undefined) {
      this.camera.target = options.target === null ? null : normalizePosition(options.target, 'options.target');
    }
    if (options.heading !== undefined) {
      if (!Number.isFinite(options.heading)) throw new RangeError('options.heading must be finite');
      this.camera.heading = options.heading;
    }
    if (options.zoom !== undefined) {
      if (!Number.isFinite(options.zoom) || options.zoom < 0.25 || options.zoom > 4) {
        throw new RangeError('options.zoom must be between 0.25 and 4');
      }
      this.camera.zoom = options.zoom;
    }
    if (options.viewRadius !== undefined) {
      if (!Number.isFinite(options.viewRadius)
        || options.viewRadius < 2
        || options.viewRadius > 100) {
        throw new RangeError('options.viewRadius must be between 2 and 100');
      }
      this.camera.viewRadius = options.viewRadius;
    }
    if (this.mode === 'engine2') this.renderNow();
    this.#syncStatus();
    return freezeCamera(this.camera);
  }

  launchRenderLab() {
    const api = this.app.testApi;
    api.resetSimulationSession({
      seed: 'engine2-render-lab',
      paused: false,
      timeOfDay: 'dusk',
      weather: 'fog',
    });
    api.addTraversalVolume({
      id: 'render-lab-water',
      kind: 'water',
      bounds: {
        min: { x: -5, y: -0.5, z: -2 },
        max: { x: 5, y: 0.2, z: 3.5 },
      },
      metadata: { surfaceY: 0.05 },
    });
    api.addTraversalVolume({
      id: 'render-lab-cover',
      kind: 'cover',
      bounds: {
        min: { x: -3.5, y: -0.2, z: 4 },
        max: { x: -0.5, y: 1, z: 6.5 },
      },
    });
    api.addTraversalVolume({
      id: 'render-lab-log',
      kind: 'crawl-under',
      bounds: {
        min: { x: 1.5, y: -0.2, z: 4.2 },
        max: { x: 4.8, y: 0.7, z: 5.3 },
      },
      metadata: { clearance: 0.35 },
    });
    api.addTraversalVolume({
      id: 'render-lab-waste',
      kind: 'hazard',
      bounds: {
        min: { x: 4.5, y: -0.3, z: -4 },
        max: { x: 7, y: 0.3, z: -1.5 },
      },
      metadata: { damagePerSecond: 3 },
    });
    api.spawnPlayerSnake({
      species: 'banded-water-snake',
      position: { x: 0, y: 0.08, z: 0 },
      heading: 0.2,
      length: 1.35,
      mass: 0.28,
      vitals: { health: 100, hunger: 24, stamina: 92, temperature: 24 },
    });
    api.spawnWildlife('bullfrog', { position: { x: -2, y: 0.08, z: 2.2 }, heading: 1.2 });
    api.spawnWildlife('marsh-insect', { position: { x: -1.2, y: 0.08, z: 4.7 } });
    api.spawnWildlife('mud-minnow', { position: { x: 2.4, y: 0.05, z: 1.4 }, heading: 2.8 });
    api.spawnWildlife('great-blue-heron', { position: { x: 4.5, y: 0.1, z: 6.2 }, heading: 3.5 });
    this.setDiagnostics({ showGrid: true, showTraversal: false, showLabels: false });
    this.setCamera({ followSnake: true, followHeading: true, zoom: 1, viewRadius: 9 });
    return this.showEngine2();
  }

  getStatus() {
    const rendererStatus = this.renderer?.getStatus?.() ?? {
      version: 1,
      started: false,
      visible: false,
      frameCount: 0,
      lastTick: null,
      lastChecksum: null,
      lastRenderMilliseconds: 0,
      lastError: null,
      width: 0,
      height: 0,
      devicePixelRatio: 1,
      wildlifeCount: 0,
      volumeCount: 0,
      actorCount: 0,
      diagnostics: this.diagnostics,
    };
    return Object.freeze({
      integrationVersion: RENDERING_INTEGRATION_VERSION,
      installed: this.installed,
      mode: this.mode,
      camera: freezeCamera(this.camera),
      diagnostics: this.diagnostics,
      lastRenderStateChecksum: this.lastRenderState?.checksum ?? null,
      lastIntegrationError: this.lastIntegrationError,
      renderer: rendererStatus,
    });
  }

  #installDom() {
    this.shell = this.mount.querySelector('.shell');
    this.legacyHost = this.mount.querySelector('[data-legacy-host]');
    this.playControls = this.mount.querySelector('[data-play-controls]');
    this.controlsMenu = this.mount.querySelector('[data-controls-menu]');
    this.controlsToggle = this.mount.querySelector('[data-action="controls-toggle"]');
    if (!this.shell || !this.legacyHost || !this.playControls || !this.controlsMenu) {
      throw new Error('GameApp shell is missing rendering integration mount points');
    }

    this.engine2Host = this.documentRef.createElement('section');
    this.engine2Host.className = 'engine2-host';
    this.engine2Host.dataset.engine2Host = '';
    this.engine2Host.hidden = true;
    this.legacyHost.before(this.engine2Host);

    const actions = this.mount.querySelector('.actions');
    const legacyButton = actions?.querySelector('[data-action="legacy"]');
    if (actions && !actions.querySelector('[data-action="engine2"]')) {
      const button = this.documentRef.createElement('button');
      button.type = 'button';
      button.dataset.action = 'engine2';
      button.textContent = 'Launch Engine 2.0 Render Lab';
      actions.insertBefore(button, legacyButton ?? actions.firstChild);
    }

    if (!this.controlsMenu.querySelector('[data-action="engine2"]')) {
      const engineButton = this.documentRef.createElement('button');
      engineButton.type = 'button';
      engineButton.dataset.action = 'engine2';
      engineButton.textContent = 'Engine 2.0';
      this.controlsMenu.prepend(engineButton);
    }
    if (!this.controlsMenu.querySelector('[data-action="legacy"]')) {
      const legacyControl = this.documentRef.createElement('button');
      legacyControl.type = 'button';
      legacyControl.dataset.action = 'legacy';
      legacyControl.textContent = 'v31';
      const exitButton = this.controlsMenu.querySelector('[data-action="exit-game"]');
      this.controlsMenu.insertBefore(legacyControl, exitButton ?? null);
    }

    const statusGrid = this.mount.querySelector('.status-grid');
    if (statusGrid && !statusGrid.querySelector('[data-rendering-status-row]')) {
      const row = this.documentRef.createElement('div');
      row.dataset.renderingStatusRow = '';
      const term = this.documentRef.createElement('dt');
      term.textContent = 'Rendering';
      this.statusNode = this.documentRef.createElement('dd');
      this.statusNode.dataset.status = 'rendering';
      this.statusNode.textContent = 'Ready';
      row.append(term, this.statusNode);
      statusGrid.append(row);
    }
  }

  #installTestLabControls() {
    const root = this.app.testLab?.root;
    const toolbar = root?.querySelector?.('.test-lab__toolbar');
    if (!root || !toolbar) return;
    if (!toolbar.querySelector('[data-action="rendering"]')) {
      const diagnosticsButton = this.documentRef.createElement('button');
      diagnosticsButton.type = 'button';
      diagnosticsButton.dataset.action = 'rendering';
      diagnosticsButton.textContent = 'Rendering';
      toolbar.append(diagnosticsButton);
    }
    if (!toolbar.querySelector('[data-action="render-lab"]')) {
      const labButton = this.documentRef.createElement('button');
      labButton.type = 'button';
      labButton.dataset.action = 'render-lab';
      labButton.textContent = 'Render Lab';
      toolbar.append(labButton);
    }
    root.addEventListener('click', this.onTestLabClick);
  }

  #installInputBridge() {
    if (!this.app.bus?.on) return;
    this.detachInputStep = this.app.bus.on('input:step', (payload) => {
      const zoom = payload?.input?.zoom ?? 0;
      if (this.mode !== 'engine2' || !Number.isFinite(zoom) || Math.abs(zoom) < 0.001) return;
      this.camera.zoom = clamp(this.camera.zoom * (1 - (zoom * 0.12)), 0.25, 4);
    });
  }

  #handleClick(event) {
    const action = event.target.closest?.('[data-action]')?.dataset.action;
    if (action === 'engine2') this.launchRenderLab();
    if (action === 'legacy') {
      this.mode = 'legacy';
      this.engine2Host.hidden = true;
      this.renderer?.setVisible(false);
      this.legacyHost.hidden = false;
      this.shell.classList.remove('render-mode-engine2');
      this.shell.classList.add('render-mode-legacy');
      this.#syncStatus();
    }
    if (action === 'exit-game') this.exitPlayMode();
  }

  #handleTestLabClick(event) {
    const action = event.target.closest?.('[data-action]')?.dataset.action;
    let result = null;
    if (action === 'rendering') result = this.app.testApi.getRenderingDiagnostics();
    if (action === 'render-lab') result = {
      ok: true,
      status: this.launchRenderLab(),
      renderState: this.captureRenderState(),
    };
    if (result) {
      const output = this.app.testLab.root.querySelector('.test-lab__output');
      if (output) output.textContent = JSON.stringify(result, null, 2);
    }
  }

  #scheduleFrame() {
    if (!this.installed || typeof this.windowRef?.requestAnimationFrame !== 'function') return;
    this.rafId = this.windowRef.requestAnimationFrame((timestamp) => {
      if (this.mode === 'engine2' && this.renderer?.visible) {
        try {
          this.renderNow(timestamp);
        } catch (error) {
          this.lastIntegrationError = error instanceof Error ? error.message : String(error);
          this.#syncStatus();
        }
      }
      this.#scheduleFrame();
    });
  }

  #syncStatus() {
    const status = this.getStatus();
    if (this.app.runtimeState) this.app.runtimeState.rendering = status;
    if (this.statusNode) {
      this.statusNode.textContent = status.mode === 'engine2'
        ? `Engine 2.0 · ${status.renderer.frameCount} frames`
        : status.mode === 'legacy'
          ? 'v31 compatibility'
          : 'Ready';
    }
    return status;
  }
}
